import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowLeft, LogOut, Gem, Trophy, BadgeCheck, Heart, MessageCircle, UserPlus, ShoppingBag, Shirt } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";
import NaijaAvatar from "@/components/NaijaAvatar";

const RARITY_COLOR = { common: "#A1A1AA", uncommon: "#16A34A", rare: "#FF0055", legendary: "#FFD700" };

export default function Profile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { character, setCharacter, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const isSelf = !id;

  const load = useCallback(async () => {
    try {
      if (isSelf) {
        const { data } = await api.get("/character");
        setProfile(data.character);
        setCharacter(data.character);
      } else {
        const { data } = await api.get(`/social/profile/${id}`);
        setProfile(data.profile);
      }
    } catch (e) { toast.error(errMsg(e)); }
  }, [id, isSelf, setCharacter]);

  useEffect(() => { load(); }, [load]);

  const p = profile || (isSelf ? character : null);
  if (!p) return null;

  const verified = p.cosmetics?.includes("verified");
  const gold = p.cosmetics?.includes("gold_frame");
  const crown = p.cosmetics?.includes("crown_title");

  const sell = async (instId) => {
    try { const { data } = await api.post("/shop/sell", { instance_id: instId }); setCharacter(data.character); setProfile(data.character); toast.success(`Sold for ${naira(data.sold_for)}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addFriend = async () => {
    try { await api.post("/social/friend-request", { target_id: id }); toast.success("Request sent 🤝"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const flirt = async () => {
    try { await api.post("/social/flirt", { target_id: id }); toast.success("Shot taken 💘"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const xpInLevel = p.xp;

  return (
    <div className="px-5 pt-6" data-testid="profile-page">
      {!isSelf && (
        <button onClick={() => nav("/social")} className="flex items-center gap-1 text-slate-500 mb-4 label-xs"><ArrowLeft size={16} /> Back</button>
      )}

      {/* Hero */}
      <div className="relative bg-white border border-slate-200 rounded-2xl p-5 overflow-hidden">
        <div className="absolute -top-10 -right-6 w-40 h-40 rounded-full blur-[70px]" style={{ background: `${p.birth?.color || "#FF0055"}30` }} />
        <div className="relative flex items-center gap-4">
          <div className="w-[72px] h-[72px] rounded-full overflow-hidden bg-slate-100 flex items-end justify-center shrink-0"
            style={{ boxShadow: `0 0 0 2px ${gold ? "#FFD700" : "#e2e8f0"}` }} data-testid="profile-avatar">
            <NaijaAvatar appearance={p.appearance} view="head" size={76} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h1 className="font-display text-2xl truncate">{crown ? "👑 " : ""}{p.name}</h1>
              {verified && <BadgeCheck size={18} className="text-[#00E5FF] shrink-0" />}
            </div>
            <div className="label-xs mt-1" style={{ color: RARITY_COLOR[p.birth?.rarity] }}>
              {p.birth?.emoji} {p.birth?.title}
            </div>
            <div className="label-xs text-slate-500 mt-1">{p.district_name}, {p.city} · Lv{p.level}</div>
          </div>
        </div>

        <div className="relative mt-5 grid grid-cols-3 gap-2">
          <Stat label="Clout" value={p.clout} color="#FF0055" />
          <Stat label="Wallet" value={isSelf ? naira(p.wallet) : "₦•••"} color="#16A34A" small />
          <Stat label="Level" value={p.level} color="#FFD700" />
        </div>
      </div>

      {/* Edit look (self only) */}
      {isSelf && (
        <button data-testid="profile-edit-look" onClick={() => nav("/wardrobe")}
          className="mt-3 w-full bg-[#16A34A] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2">
          <Shirt size={18} /> Change my look
        </button>
      )}

      {/* Actions for other player */}
      {!isSelf && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button data-testid="profile-flirt" onClick={flirt} className="bg-[#FF0055]/15 text-[#FF0055] py-3 rounded-xl flex items-center justify-center gap-2 label-xs"><Heart size={16} /> Flirt</button>
          {p.is_friend ? (
            <button onClick={() => nav(`/chat/${id}`)} className="bg-[#16A34A]/15 text-[#16A34A] py-3 rounded-xl flex items-center justify-center gap-2 label-xs"><MessageCircle size={16} /> Message</button>
          ) : (
            <button data-testid="profile-add" onClick={addFriend} className="bg-[#00E5FF]/15 text-[#00E5FF] py-3 rounded-xl flex items-center justify-center gap-2 label-xs"><UserPlus size={16} /> Add</button>
          )}
          <button onClick={() => nav(`/chat/${id}`)} className="bg-white border border-slate-200 text-slate-600 py-3 rounded-xl flex items-center justify-center gap-2 label-xs"><MessageCircle size={16} /> DM</button>
        </div>
      )}

      {/* Showcase */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <span className="label-xs text-slate-500">Flex Showcase</span>
          <span className="label-xs text-slate-400">{p.inventory?.length || 0} items</span>
        </div>
        {p.inventory?.length === 0 ? (
          <button onClick={() => isSelf && nav("/shop")} className="w-full bg-white border border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center gap-2 text-slate-400">
            <ShoppingBag size={24} />
            <span className="text-sm">{isSelf ? "Empty. Go cop something to flex." : "No flex yet."}</span>
          </button>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {p.inventory.map((it) => {
              const Icon = Icons[it.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Icons.ShoppingBag;
              return (
                <div key={it.instance_id} data-testid={`inv-${it.item_id}`} className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <Icon size={20} className="text-[#FFD700]" />
                    <span className="label-xs text-[#FF0055]">+{it.clout}</span>
                  </div>
                  <div className="mt-2 text-sm font-semibold truncate">{it.title}</div>
                  {it.value != null && <div className="label-xs text-slate-500 mt-0.5">worth {naira(it.value)}</div>}
                  {isSelf && (
                    <button data-testid={`sell-${it.instance_id}`} onClick={() => sell(it.instance_id)} className="mt-2 w-full py-1.5 rounded-lg label-xs bg-black/50 text-slate-500 hover:text-[#FF0055] transition-colors">Sell</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isSelf && (
        <div className="mt-6 space-y-2 mb-4">
          <button data-testid="profile-store" onClick={() => nav("/store")} className="w-full bg-[#FFD700] text-slate-900 font-bold py-3.5 rounded-xl flex items-center justify-center gap-2"><Gem size={18} /> Premium Store ({p.gems}💎)</button>
          <button data-testid="profile-board" onClick={() => nav("/leaderboard")} className="w-full bg-white border border-slate-200 text-slate-900 py-3.5 rounded-xl flex items-center justify-center gap-2"><Trophy size={18} className="text-[#FFD700]" /> Leaderboard</button>
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="w-full bg-white border border-[#FF0055]/30 text-[#FF0055] py-3.5 rounded-xl flex items-center justify-center gap-2"><LogOut size={18} /> Log Out</button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, small }) {
  return (
    <div className="bg-black/40 rounded-xl p-3">
      <div className="label-xs text-slate-500">{label}</div>
      <div className={`font-display mt-1 neon-text ${small ? "text-sm" : "text-lg"}`} style={{ color }}>{value}</div>
    </div>
  );
}
